import React from "react"

import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { BULK_RESULTS_INSIGHTS_CONFIG } from "../../../config/bulkResultsInsights.config"
import type {
    IBulkResultsFilter,
    IBulkResultsPatientRow,
} from "../../../types/bulkResultsInsights.types"
import { ResultsDownloadComponent } from "../resultsDownload.component"

import magicWandWhiteIcon from "@front-shared/assets/images/icons/magic-wand-white.svg"

export const BulkResultsTableComponent: React.FunctionComponent<
    IBulkResultsTableComponentProps
> = props => {
    const visibleRows = props.rows.slice(0, props.visibleCount)
    const remaining = props.rows.length - visibleRows.length

    return (
        <div
            className={`rounded-[16px] bg-brand-white p-[24px] shadow-sm ${
                props.className || ""
            }`}
        >
            <div className="mb-[16px] flex flex-wrap items-center gap-[12px]">
                <PrimaryText typography="h5">Results</PrimaryText>

                <PrimaryText typography="subtitle" className="opacity-60">
                    {props.filter
                        ? `${props.rows.length} of ${props.totalPatients} patients`
                        : `${props.totalPatients} patients`}
                </PrimaryText>

                {props.filter ? (
                    <button
                        type="button"
                        className="rounded-full border-0 bg-brand-lilacLight px-[12px] py-[4px]"
                        onClick={props.onClearFilter}
                    >
                        <PrimaryText>
                            {props.filter.label}
                            <span className="ml-[8px] opacity-60">×</span>
                        </PrimaryText>
                    </button>
                ) : null}

                <div className="ml-auto">
                    <ResultsDownloadComponent
                        variant="inline"
                        includeInputIds={
                            props.filter
                                ? props.rows.map(row => row.id)
                                : undefined
                        }
                        label={
                            props.filter
                                ? `Download ${props.rows.length} filtered result${
                                      props.rows.length === 1 ? "" : "s"
                                  }`
                                : `Download CSV with ${props.totalPatients} result${
                                      props.totalPatients === 1 ? "" : "s"
                                  }`
                        }
                    />
                </div>
            </div>

            <div className="overflow-x-auto">
                <div className="min-w-[720px]">
                    <div className="grid grid-cols-[1.4fr_1.2fr_1.4fr_1.6fr_0.8fr] gap-[12px] border-0 border-b border-solid border-brand-warmLight2 py-[8px]">
                        {[
                            "Patient",
                            "Payer",
                            "Outcome",
                            "Cost share",
                            "Findings",
                        ].map(header => (
                            <PrimaryText
                                key={header}
                                typography="subtitle"
                                className="uppercase opacity-50"
                            >
                                {header}
                            </PrimaryText>
                        ))}
                    </div>

                    {visibleRows.map(row => (
                        <div
                            key={row.id}
                            className="grid grid-cols-[1.4fr_1.2fr_1.4fr_1.6fr_0.8fr] items-center gap-[12px] border-0 border-b border-solid border-brand-warmLight2 py-[12px]"
                        >
                            <div>
                                <PrimaryText weight="medium">
                                    {row.patientName}
                                </PrimaryText>

                                {row.isSmartScanRecovered ? (
                                    <span
                                        className="
                                            mt-[4px] inline-flex items-center gap-[4px] whitespace-nowrap
                                            rounded-full bg-brand-vibrantPurple px-[8px] py-[2px]
                                        "
                                    >
                                        <img
                                            src={magicWandWhiteIcon}
                                            alt=""
                                            className="h-[12px] w-[12px]"
                                        />

                                        <PrimaryText
                                            typography="captionSemibold"
                                            color="white"
                                        >
                                            Updated by SmartScan
                                        </PrimaryText>
                                    </span>
                                ) : null}
                            </div>

                            <PrimaryText>{row.payerName}</PrimaryText>

                            <div className="flex items-center gap-[8px]">
                                <span
                                    className="h-[12px] w-[12px] flex-shrink-0 rounded-[2px]"
                                    style={{
                                        backgroundColor:
                                            BULK_RESULTS_INSIGHTS_CONFIG
                                                .outcomeColors[row.outcome],
                                    }}
                                />

                                <PrimaryText>
                                    {
                                        BULK_RESULTS_INSIGHTS_CONFIG
                                            .outcomeLabels[row.outcome]
                                    }
                                </PrimaryText>
                            </div>

                            <PrimaryText>{row.costShareLabel}</PrimaryText>

                            <div className="flex flex-wrap gap-[4px]">
                                {row.findingTags.map(tag => (
                                    <span
                                        key={tag.finding}
                                        className="rounded-full bg-brand-lilacLight px-[8px] py-[2px]"
                                    >
                                        <PrimaryText typography="subtitle">
                                            {tag.label}
                                        </PrimaryText>
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-[16px] flex items-center gap-[8px]">
                <PrimaryText className="opacity-60">
                    Showing {visibleRows.length} of {props.rows.length}
                </PrimaryText>

                {remaining > 0 ? (
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 text-brand-vibrantPurple"
                        onClick={props.onLoadMore}
                    >
                        <PrimaryText weight="medium" color="inherit">
                            Load more
                        </PrimaryText>
                    </button>
                ) : null}
            </div>
        </div>
    )
}

interface IBulkResultsTableComponentProps {
    className?: string
    rows: IBulkResultsPatientRow[]
    totalPatients: number
    visibleCount: number
    filter: IBulkResultsFilter | null
    onClearFilter: () => void
    onLoadMore: () => void
}
